/**
 * Decides which NetInfo observations are worth publishing as `application:network` updates.
 *
 * Lives apart from `network.native.js` so it can be unit tested without pulling in
 * `@react-native-community/netinfo` (and with it, all of react-native).
 *
 * `doingUpdate_application_network` resyncs off these events, and only when the payload's
 * `isOnline` is truthy. So the contract this filter has to hold is narrow but strict:
 *
 * - publish every transition **into** being online, or the app loses its recovery sync for
 *   the rest of the session
 * - publish every transition **out of** being online, or the user gets no "connection lost"
 * - publish nothing merely because we subscribed, or every cold start runs a second full sync
 *   behind the orchestrator's own startup sync
 *
 * Resume is not this module's job - `App.ts` syncs on the AppState `active` transition.
 */

/**
 * @typedef {{isConnected?: boolean|null, isInternetReachable?: boolean|null, type?: string}} NetworkObservation
 */

/**
 * Whether an observation tells us anything actionable yet.
 *
 * `isInternetReachable` is `null` until NetInfo's reachability probe answers. While we are
 * connected that means "not known yet", so the observation carries no verdict - note that
 * `network.native.js` publishes `isOnline: isConnected && isInternetReachable`, which is `null`
 * here, and the rule treats that as not-online. A disconnected radio needs no probe.
 *
 * @param {NetworkObservation} state
 */
function isDetermined(state) {
    if (state.isConnected === false) return true;
    return state.isConnected === true && typeof state.isInternetReachable === 'boolean';
}

/**
 * @param {NetworkObservation} state
 */
function isOnline(state) {
    return state.isConnected === true && state.isInternetReachable === true;
}

/**
 * Build a stateful predicate answering "should this observation be published?".
 *
 * Publishes exactly the changes in effective online status, measured against a baseline taken
 * at subscribe time rather than against the previous observation.
 *
 * Baselining is what makes cold start quiet. `NetInfo.addEventListener` replays its latest known
 * state to every new subscriber immediately; `listenForChange` is only ever called from
 * `orchestrateUseCase`, which syncs unconditionally moments later, so that replay is the state
 * we started in and never a recovery. If the probe is still pending at that moment the baseline
 * stays open until the first observation that carries a verdict - the probe settling during
 * startup is the baseline being established, not the network coming back.
 *
 * Measuring against the baseline rather than the previous observation is the part that matters:
 * comparing against the previous observation swallows the real recovery sequence
 * `offline -> connected/probe-pending -> connected/reachable`, because its last step also looks
 * like a probe settling.
 *
 * @returns {(state: NetworkObservation) => boolean}
 */
export default function createNetworkChangeFilter() {
    // `null` until an observation carries a verdict; from then on the last published verdict.
    /** @type {boolean|null} */
    let lastKnownOnline = null;
    let baselineEstablished = false;

    return function shouldPublish(state) {
        if (!isDetermined(state)) return false;

        const online = isOnline(state);

        if (!baselineEstablished) {
            baselineEstablished = true;
            lastKnownOnline = online;
            return false;
        }

        if (online === lastKnownOnline) return false;

        lastKnownOnline = online;
        return true;
    };
}
